/**
 * THE ONLY PLACE THIS FEATURE NAMES THE API CONTRACT.
 *
 * Every request and response shape below is an alias into
 * `shared/types/generated/authors-api.d.ts`, which is generated FROM the committed OpenAPI spec
 * (`pnpm gen:types`, `tools/openapi/generate.mjs`). Nothing here is hand-written, and nothing here
 * may be widened by hand: if a field is missing from these aliases it is missing from the contract,
 * and that is a finding rather than something a screen invents.
 *
 * WHY A RELATIVE IMPORT ACROSS THE WORKSPACE. `web/authors/package.json` does not (yet) depend on
 * `@moonx/types`, so the package name does not resolve from this app — and adding the dependency
 * touches a file this track does not own. `adr/0013` rules "no path aliases — plain relative
 * imports" and the root tsconfig has no `paths` entry (a build-plan assertion greps for one), so a
 * relative import is the sanctioned mechanism either way. It is `import type`, so with
 * `verbatimModuleSyntax` it is erased entirely and no runtime edge is created.
 */
import type { components, operations } from '../../../../../../shared/types/generated/authors-api';

type Schemas = components['schemas'];

/**
 * The two contact addresses. BOTH KEYS ARE ALWAYS PRESENT AND EITHER VALUE MAY BE NULL —
 * `readonly supportEmail: string | null` in the generated types, `required` in the spec. So every
 * branch in this feature tests the VALUE, never key presence.
 */
export type PlatformContacts = Schemas['PlatformContacts'];

/**
 * The only terms payload the cabinet ever sees. Note what it does NOT carry: no `documentCode`, no
 * document label. See `TERMS_DOC_KEY` in ../strings.ts.
 */
export type TermsState = Schemas['TermsState'];

/**
 * `{ versionLabel: string }` and nothing else. The spec is `additionalProperties: false` with
 * `versionLabel` as its only property, which is why THE CLIENT NEVER SENDS THE ACCEPTANCE CHANNEL:
 * `terms_acceptance_channel` is a two-value Postgres enum (`'application'`, `'cabinet_reaccept'`)
 * and the SERVER decides which one this call writes (`04-schema.md`, `:1015-1019`). A body
 * carrying anything else is a 400.
 */
export type TermsAcceptanceRequest = Schemas['TermsAcceptanceRequest'];

/** `GET /v1/me` — one read renders the app. `termsAcceptanceRequired` is read from here. */
export type AuthorSession = Schemas['AuthorSession'];

/** RFC 9457 problem body. `error_code` is the contract; `title` and `detail` are not. */
export type Problem = Schemas['Problem'];

/** The 65 registered machine codes. A client branches on these and on nothing else. */
export type ErrorCode = Schemas['ErrorCode'];

/**
 * The operation ids this feature calls, spelled once. Referencing `operations` keeps the compiler
 * honest: rename an operation in the spec and this file stops building.
 */
export type SupportOperations = Pick<
  operations,
  'getPlatformContacts' | 'getCurrentTerms' | 'acceptTerms' | 'getAuthorSession'
>;

/**
 * The paths, spelled once, as they appear in the spec's `paths` object. The base URL is prepended
 * by the transport; see ./transport.ts.
 */
export const API_PATHS = {
  authorSession: '/v1/me',
  platformContacts: '/v1/platform-contacts',
  currentTerms: '/v1/terms/current',
  termsAcceptances: '/v1/terms/acceptances',
  agreements: '/v1/agreements',
  agreementAcceptances: '/v1/agreements/acceptances',
} as const;

/**
 * ★ DESIGN 25'S AGREEMENTS (`listAgreements`, `acceptAgreements`; section 3.1): every document with
 * a version in force or one the author accepted, and the acceptance of several in one call.
 */
export type Agreement = Schemas['Agreement'];
export type AgreementBlocks = Agreement['blocks'];
export type AgreementList = Schemas['AgreementList'];
export type AgreementAcceptRequest = Schemas['AgreementAcceptRequest'];
